// Enhanced logging utility with environment-based controls
import { config } from "../../config";

interface LogOptions {
  force?: boolean; // Force logging even if disabled
  prefix?: string; // Custom prefix for logs
}

class Logger {
  private static shouldLog(force = false): boolean {
    return force || config.enableConsoleLogs;
  }

  static log(message: any, ...args: any[]): void;
  static log(message: any, options: LogOptions, ...args: any[]): void;
  static log(
    message: any,
    optionsOrArgs?: LogOptions | any,
    ...restArgs: any[]
  ): void {
    const isOptionsObject =
      optionsOrArgs &&
      typeof optionsOrArgs === "object" &&
      !Array.isArray(optionsOrArgs) &&
      optionsOrArgs.constructor === Object;
    const options: LogOptions = isOptionsObject ? optionsOrArgs : {};
    const args = isOptionsObject ? restArgs : [optionsOrArgs, ...restArgs];

    if (!this.shouldLog(options.force)) return;

    const prefix = options.prefix ? `[${options.prefix}] ` : "";
    console.log(`${prefix}${message}`, ...args);
  }

  static warn(message: any, ...args: any[]): void;
  static warn(message: any, options: LogOptions, ...args: any[]): void;
  static warn(
    message: any,
    optionsOrArgs?: LogOptions | any,
    ...restArgs: any[]
  ): void {
    const isOptionsObject =
      optionsOrArgs &&
      typeof optionsOrArgs === "object" &&
      !Array.isArray(optionsOrArgs) &&
      optionsOrArgs.constructor === Object;
    const options: LogOptions = isOptionsObject ? optionsOrArgs : {};
    const args = isOptionsObject ? restArgs : [optionsOrArgs, ...restArgs];

    if (!this.shouldLog(options.force)) return;

    const prefix = options.prefix ? `[${options.prefix}] ` : "";
    console.warn(`${prefix}${message}`, ...args);
  }

  static error(message: any, ...args: any[]): void;
  static error(message: any, options: LogOptions, ...args: any[]): void;
  static error(
    message: any,
    optionsOrArgs?: LogOptions | any,
    ...restArgs: any[]
  ): void {
    const isOptionsObject =
      optionsOrArgs &&
      typeof optionsOrArgs === "object" &&
      !Array.isArray(optionsOrArgs) &&
      optionsOrArgs.constructor === Object;
    const options: LogOptions = isOptionsObject ? optionsOrArgs : {};
    const args = isOptionsObject ? restArgs : [optionsOrArgs, ...restArgs];

    // Errors are always logged unless explicitly disabled
    if (!this.shouldLog(true)) return;

    const prefix = options.prefix ? `[${options.prefix}] ` : "";
    console.error(`${prefix}${message}`, ...args);
  }

  static debug(message: any, ...args: any[]): void;
  static debug(message: any, options: LogOptions, ...args: any[]): void;
  static debug(
    message: any,
    optionsOrArgs?: LogOptions | any,
    ...restArgs: any[]
  ): void {
    const isOptionsObject =
      optionsOrArgs &&
      typeof optionsOrArgs === "object" &&
      !Array.isArray(optionsOrArgs) &&
      optionsOrArgs.constructor === Object;
    const options: LogOptions = isOptionsObject ? optionsOrArgs : {};
    const args = isOptionsObject ? restArgs : [optionsOrArgs, ...restArgs];

    // Debug logs only show when debug is enabled AND console logs are enabled
    if (!config.enableDebug || !this.shouldLog(options.force)) return;

    const prefix = options.prefix
      ? `[DEBUG${options.prefix ? ` ${options.prefix}` : ""}] `
      : "[DEBUG] ";
    console.log(`${prefix}${message}`, ...args);
  }

  // Security-aware logging (never logs sensitive data)
  static security(message: any, options: LogOptions = {}): void {
    if (!config.enableDebug) return;

    const prefix = `[SECURITY${options.prefix ? ` ${options.prefix}` : ""}] `;
    console.log(`${prefix}${message}`);
  }

  // Development-only logging
  static dev(message: any, ...args: any[]): void {
    if (!config.isDevelopment() || !this.shouldLog()) return;

    console.log(`[DEV] ${message}`, ...args);
  }
}

// Export both the class and convenience functions
export default Logger;
export const log = Logger.log.bind(Logger);
export const warn = Logger.warn.bind(Logger);
export const error = Logger.error.bind(Logger);
export const debug = Logger.debug.bind(Logger);
export const security = Logger.security.bind(Logger);
export const dev = Logger.dev.bind(Logger);
